import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    Box,
    Stack,
    Chip,
    Button,
    Grid,
    Tooltip,
    IconButton,
} from '@mui/material';
import { BANK_ACCOUNTS, BankAccountItem } from '../../constants/bankAccounts';
import BinanceLogo from "../../assets/images/svg/binance.png";
import PaypalLogo from "../../assets/images/svg/paypal.png";
import { getPrimaryColorByAccountId, getThemeByAccountId } from '../../utils/styleConstants';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/types/rootState';
import { useAppSelector } from '../../store/hooks/useAppSelector';
import { useGetRaffleByIdQuery } from '../../store/api';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { banks, getIconFromBankId } from '../../constants/banks';
import { ContentCopy } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';



interface BankAccountsSelectorProps {
    accountId: string;
    ticketSelectedCount: number;
    bankAccounts: BankAccountItem[];
    setSelectedBankAccount: Dispatch<SetStateAction<BankAccountItem | undefined>>;
    templateId?: string;
}

export const BankAccountSelector = ({
  accountId,
  bankAccounts,
  ticketSelectedCount,
  setSelectedBankAccount,
  templateId,
}: BankAccountsSelectorProps) => {
  const [selectedBankAccount, setSelectedBankAccountAux] = useState<BankAccountItem>();

  useEffect(() => {
    if (bankAccounts.length > 0) {
      setSelectedBankAccountAux(bankAccounts[0]);
      setSelectedBankAccount(bankAccounts[0]);
    }
  }, [bankAccounts]);

  const isSelected = (element: BankAccountItem) =>
    selectedBankAccount?.accountNumber?.concat(selectedBankAccount?.bankId || '') ===
    element.accountNumber?.concat(element.bankId || '');

  return (
    <Box mt={2}>
      <Grid container justifyContent="center" spacing={3} mb={3}>
        {bankAccounts.map(
          (element, index) =>
            !element.hide && (
              <Grid item xs={4} sm={3} md={2} key={`${element.bankId}-${index}`}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    height: templateId === 'default' ? 80 : 60,
                    borderRadius: templateId === 'default' ? 2 : 1,
                    p: 1,
                    border: 'none',
                    borderBottom: `2px solid ${getPrimaryColorByAccountId(accountId)}`,
                    borderColor: isSelected(element)
                      ? templateId === 'default'
                        ? 'primary.main'
                        : undefined
                      : undefined,
                    opacity: isSelected(element) ? 1 : 0.4,
                    boxShadow: templateId === 'default' ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                  }}
                  onClick={() => {
                    setSelectedBankAccount(element);
                    setSelectedBankAccountAux(element);
                  }}
                >
                  <Box display="flex" alignItems="center" flexDirection="column">
                    {banks.find((bank) => bank.id === element.bankId)?.icon}
                    <Typography variant="body2" fontSize={12}  color={getThemeByAccountId(accountId) !== 'dark' ? '#000 !important' : '#FFF !important' }>
                      {banks.find((bank) => bank.id === element.bankId)?.name}
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            )
        )}
      </Grid>

      {selectedBankAccount && (
        <BankAccount
          item={selectedBankAccount}
          ticketSelectedCount={ticketSelectedCount}
          templateId={templateId || 'default'}
        />
      )}
    </Box>
  );
};

interface BankAccountsProps {
    ticketSelectedCount: number; 
    accountId: string;
    raffleId: string;
    setSelectedBankAccount: Dispatch<SetStateAction<BankAccountItem | undefined>>;
}

const BankAccounts = ({ticketSelectedCount, accountId, raffleId, setSelectedBankAccount}: BankAccountsProps) => {

    
    const { data: activeRaffleFounded, isFetching } = useGetRaffleByIdQuery(raffleId, {
    skip: raffleId === "default" || raffleId === "",
    });
    const [bankAccounts, setBankAccounts] = useState<BankAccountItem[]>([]);

    useEffect(() => {
        if (activeRaffleFounded && activeRaffleFounded.bankAccounts) {
            setBankAccounts(activeRaffleFounded.bankAccounts)
        } else if (!isFetching) {
            // Solo usar las cuentas estáticas cuando la rifa ya respondió sin cuentas (o falló)
            const config = BANK_ACCOUNTS.find(bankAccount => bankAccount.accountId === accountId);
            if (config != undefined) {
                setBankAccounts(config.bankAccounts)
            }
        }
    }, [activeRaffleFounded, isFetching, accountId]);
    

    
    if (!bankAccounts) {
        return <></>
    }

    return (
        <>
            <Typography variant='h6' fontWeight={"bold"}  textAlign={"center"}>
                ¿A donde {accountId === 'sobre-ruedas-ok' ? 'querés' : 'quieres'} transferir? <br/> Selecciona una cuenta:
            </Typography>
            {renderBankAccounts(accountId, bankAccounts, ticketSelectedCount, setSelectedBankAccount, activeRaffleFounded?.templateId)}
            {accountId === 'los-ninos' && <Typography variant='body2' textAlign={"center"}>
                Si no cuentas con ninguna de estas cuentas, comunicate con el <a href={`https://wa.me/+5691111111/?text=Quiero comprar mis números, pero no tengo ninguna de esas cuentas`}>whatsapp de soporte.</a>
            </Typography>}
            <br />
        </>
    );
}
const renderBankAccounts = (accountId: string, bankAccounts: BankAccountItem[], ticketSelectedCount: number, setSelectedBankAccount: any, templateId: string = "template1") => {
    return bankAccounts.length > 0 ? (
                <BankAccountSelector 
                    accountId={accountId}
                    setSelectedBankAccount={setSelectedBankAccount} 
                    bankAccounts={bankAccounts} 
                    ticketSelectedCount={ticketSelectedCount} 
                    templateId={templateId}
                />
            ) : null;
};


interface BankAccountProps {
    item: BankAccountItem;
    ticketSelectedCount: number;
    templateId: string;
}

export function BankAccount({ item, ticketSelectedCount, templateId }: BankAccountProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const lines = [
            `Banco: ${item.bank}`,
            item.dni ? `DNI: ${item.dni}` : '',
            item.name ? `Nombre: ${item.name}` : '',
            item.accountType ? `Tipo de cuenta: ${item.accountType}` : '',
            item.accountNumber ? `N° de cuenta: ${item.accountNumber}` : '',
            item.phone ? `N° de teléfono: ${item.phone}` : '',
        ];

        const text = lines.filter(Boolean).join('\n');
        navigator.clipboard.writeText(text);
        setCopied(true);
          enqueueSnackbar(`Se copiaron los datos!`, {
            variant: "info",
            style: {color: 'black'},
            anchorOrigin: {
                horizontal: "right",
                vertical: "bottom"
            },
        })
        setTimeout(() => setCopied(false), 1500);
    };

    const handleCopyOne = (text: string) => {
        
        if (text.includes("Binance ID: ")) {
            text = extractBinanceId(text)
        }

        navigator.clipboard.writeText(text);
        setCopied(true);
          enqueueSnackbar(`Se copiaron los datos!`, {
            variant: "info",
            style: {color: 'black'},
            anchorOrigin: {
                horizontal: "right",
                vertical: "bottom"
            },
        })
        setTimeout(() => setCopied(false), 1500);
    };

    function extractBinanceId(text: string) {
        if (text.includes("Binance ID: ")) {
            const index = text.indexOf("Binance ID: ") + "Binance ID: ".length;
            const id = text.substring(index).trim(); // Trim to remove extra spaces or quotes
            return id;
        }
        return text;
    }

    const accountId = useSelector(({ account }: RootState) => account.id);
    const paperStyle = {
        padding: '30px 10px',
        borderRadius: '10px',
        margin: '2px',
        marginBottom: '10px',
        background: 'transparent',
        borderBottom: `2px solid ${getPrimaryColorByAccountId(accountId)}`,
        borderRight: `2px solid ${getPrimaryColorByAccountId(accountId)}`,
        width: '100%'
    };

    return !item.hide ? (
        <Paper style={paperStyle} elevation={10}>
            <Typography variant='h5' fontWeight={"bold"}>
                {item.bank}
                <span>     </span>
                {item.bankId && getIconFromBankId({
                    bankId: item.bankId,
                    height: 25,
                    width: 25
                })}
             </Typography>
            <List>
                {item.dni && (
                    <ListItem>
                        <ListItemText primary={`DNI: ${item.dni}`} />
                        <IconButton onClick={() => handleCopyOne(`${item.dni}`)} size="small" color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </ListItem>
                )}
                {item.name && (
                    <ListItem>
                        <ListItemText  primary={`Nombre: ${item.name}`} />
                        <IconButton onClick={() => handleCopyOne(`Nombre: ${item.name}`)} size="small" color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </ListItem>
                )}
                {item.accountType && (
                    <ListItem>
                        <ListItemText  primary={`Tipo de cuenta: ${item.accountType}`} />
                        <IconButton onClick={() => handleCopyOne(`${item.accountType}`)} size="small" color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </ListItem>
                )}
                {item.address && (
                    <ListItem>
                        <ListItemText primary={`Dirección: ${item.address}`} />
                        <IconButton onClick={() => handleCopyOne(`${item.dni}`)} size="small" color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </ListItem>
                )}
                {item.accountNumber && item.accountNumber !== '-' && item.accountNumber !== 'Cuenta no identidiaca' &&  (
                    <ListItem>
                        <ListItemText  primary={`N° de cuenta: ${item.accountNumber}`} />
                        <IconButton onClick={() => handleCopyOne(`${item.accountNumber}`)} size="small" color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </ListItem>
                )}
                {item.href && item.href !== '-' && item.href !== 'https://yourbankwebsite-chile.com' &&  (
                    <ListItem>
                        <ListItemText  primary={`Correo: ${item.href}`} />
                        <IconButton onClick={() => handleCopyOne(`${item.href}`)} size="small" color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </ListItem>
                )}
                {item.phone && (
                    <ListItem>
                        <ListItemText  primary={`N° de teléfono: ${item.phone}`} />
                        <IconButton onClick={() => handleCopyOne(`${item.phone}`)} size="small" color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </ListItem>
                )}
                {item.ticketPrice && (
                    <ListItem>
                        <ListItemText  primary={`Precio del ticket: ${item.ticketPrice}`} />
                    </ListItem>
                )}
                {item.conversion && item.conversion.dolarPrice != 0 && (
                    <ListItem sx={{ 
                        backgroundColor: `${getPrimaryColorByAccountId(accountId)} !important`,
                        borderRadius: 2,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <Typography textAlign={"center"} color={`${getThemeByAccountId(accountId) === 'dark' ? '#000 !important' : '#FFF !important' }`} fontWeight={"bold"}>
                            Conversión: {`1$ = ${item.conversion.dolarPrice} ${item.conversion.dolarPriceCurrency}`}
                        </Typography>
                        { ticketSelectedCount > 0 &&
                            <Typography textAlign={"center"} color={`${getThemeByAccountId(accountId) === 'dark' ? '#000 !important' : '#FFF !important' }`} fontWeight={"bold"}>
                                {`${ticketSelectedCount} x ${item.ticketPrice} = ${(item.ticketPriceAmount * ticketSelectedCount).toFixed(2)} ${item.ticketCurrency}`}
                            </Typography>
                        }
                    </ListItem>
                )}
                 {ticketSelectedCount > 0 && (
                    <ListItem sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                        <Typography variant='h5' fontWeight={"bold"} color={"primary"} textAlign={"center"}>
                            {`Total a transferir`}
                        </Typography>
                        <Typography variant='h5' fontWeight={"bold"} color={"primary"} textAlign={"center"}>
                            {`${(item.ticketPriceAmount * ticketSelectedCount).toFixed(2)} ${item.ticketCurrency}`}
                        </Typography>
                    </ListItem>
                )}
            </List>
        </Paper>
    ) : (<></>)
}

export default BankAccounts;
